import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import ProjectArt from '../components/ProjectArt';
import { ArrowRight, Close } from '../components/Icons';
import usePageTitle from '../hooks/usePageTitle';
import { PROJECTS, PROJECT_CATEGORIES } from '../data/content';
import './PortfolioPage.css';

// Native <dialog>: focus trapping, Escape to close and the backdrop come for free.
const ProjectDialog = ({ project, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    if (project && !dialog.open) dialog.showModal();
    if (!project && dialog.open) dialog.close();
  }, [project]);

  const handleBackdrop = (e) => {
    if (e.target === ref.current) onClose();
  };

  return (
    <dialog ref={ref} className="project-dialog" onClose={onClose} onClick={handleBackdrop} aria-labelledby="pd-title">
      {project && (
        <div className="pd-inner">
          <div className="pd-art">
            <ProjectArt seed={project.id} hue={project.hue} />
            <button type="button" className="icon-btn pd-close" onClick={onClose} aria-label="Close project details">
              <Close />
            </button>
          </div>
          <div className="pd-body">
            <div className="pd-meta">
              <span className="tag">{project.category}</span>
              <span className="tag">{project.year}</span>
            </div>
            <h2 id="pd-title">{project.title}</h2>
            <p className="lead">{project.description}</p>
            <div className="pd-columns">
              <div>
                <h3>The challenge</h3>
                <p>{project.challenge}</p>
              </div>
              <div>
                <h3>Our approach</h3>
                <p>{project.approach}</p>
              </div>
            </div>
            <h3>Built with</h3>
            <div className="pd-tech">
              {project.technologies.map((tech) => (
                <span key={tech} className="tag">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </dialog>
  );
};

const PortfolioPage = () => {
  usePageTitle('Portfolio');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState(null);

  const filtered = activeCategory === 'All' ? PROJECTS : PROJECTS.filter((p) => p.category === activeCategory);
  const countFor = (cat) => (cat === 'All' ? PROJECTS.length : PROJECTS.filter((p) => p.category === cat).length);

  return (
    <div className="portfolio-page">
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow">Portfolio</p>
          <h1>
            Work that <span className="highlight">includes everyone.</span>
          </h1>
          <p className="lead">Explore our recent projects and success stories.</p>
        </div>
      </section>

      <section className="section portfolio-section">
        <div className="container">
          <div className="filter-bar" role="group" aria-label="Filter projects by category">
            {PROJECT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className="filter-btn"
                aria-pressed={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
                <span className="filter-count">{countFor(cat)}</span>
              </button>
            ))}
          </div>

          <p className="sr-only" aria-live="polite">
            Showing {filtered.length} {filtered.length === 1 ? 'project' : 'projects'}
          </p>

          <div className="projects-grid" key={activeCategory}>
            {filtered.map((project, i) => (
              <Reveal key={project.id} delay={i * 70}>
                <article className="project-card card">
                  <button
                    type="button"
                    className="project-hit"
                    onClick={() => setSelected(project)}
                    aria-label={`View details: ${project.title}`}
                  />
                  <div className="project-art-wrap">
                    <ProjectArt seed={project.id} hue={project.hue} />
                    <span className="project-view" aria-hidden="true">
                      View details <ArrowRight />
                    </span>
                  </div>
                  <div className="project-content">
                    <div className="project-header">
                      <span className="tag">{project.category}</span>
                      <span className="project-year">{project.year}</span>
                    </div>
                    <h2>{project.title}</h2>
                    <p>{project.description}</p>
                    <div className="project-tech">
                      {project.technologies.map((tech) => (
                        <span key={tech}>{tech}</span>
                      ))}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <Reveal className="portfolio-cta">
            <div>
              <h2>Have a project in mind?</h2>
              <p className="lead">We'd love to help bring your vision to life with our unique approach to development.</p>
            </div>
            <Link to="/contact" className="btn">
              Get in touch <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </section>

      <ProjectDialog project={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default PortfolioPage;
